import { FormHelperText } from "@mui/material";
import { useMprcTheme } from "components/MprcThemeProvider";
import { QuillUnprivilegedEditor } from "components/Quill";
import ClipboardExt from "components/Quill/ClipboardExt";
import MagicUrl from "components/Quill/MagicUrl";
import { QuillTranslateToolbar } from "components/Quill/QuillTranslateToolbar";
import { EmitterSource } from "quill";
import Delta from "quill-delta";
import * as React from "react";
import { useEffect } from "react";
import { useQuill } from 'react-quilljs';

///////////////////////////////////////////////////////////////////////////////

interface IProps {
    id?: string;
    initialValue?: string | null;
    error?: boolean;
    helperText?: React.ReactNode;
    autoFocus?: boolean;
    disabled?: boolean;
    placeholder?: string;
    theme?: "snow" | "bubble";
    toolbar?: boolean;
    onChange: (content: string, delta: Delta, source: EmitterSource, editor: QuillUnprivilegedEditor) => void;
}

type Props = IProps;

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export const MprcQuillTextBox: React.FC<Props> = (props) => {
    const { autoFocus = false, error, helperText, placeholder, disabled = false, theme = "snow", onChange } = props;
    const { theme: mprcTheme } = useMprcTheme();
    const id = props.id || `ed_${new Date().getTime()}`;

    const formats: string[] =
        props.toolbar
            ? [
                'header', 'font', 'size', 'align', 'color', 'background',
                'bold', 'italic', 'underline', 'strike', 'blockquote', 'code-block',
                'list', 'bullet', 'indent', 'sub', 'super', 'direction'
            ]
            : [];


    /*
     * Quill modules to attach to editor
     * See https://quilljs.com/docs/modules/ for complete options
     */
    const modules = {
        toolbar: props.toolbar || false,
        magicUrl: true,
        clipboardExt: {
            image: true,
            video: false,
            youtube: true,
        },
    };


    const { quill, quillRef, Quill } = useQuill({ theme, modules, formats, placeholder });

    if (Quill && !quill) {
        // For execute this line only once.
        Quill.register('modules/magicUrl', MagicUrl);
        Quill.register('modules/clipboardExt', ClipboardExt);
    }

    const quillOnChangeHandle = React.useCallback((delta: Delta, oldDelta: Delta, source: EmitterSource) => {
        if (quill) {
            onChange(
                quill.root.innerText,
                delta,
                source,
                {
                    getBounds: quill.getBounds,
                    getLength: quill.getLength,
                    getText: (index?: number, length?: number) => quill.getText(index, length),
                    getHTML: () => quill.root.innerHTML,
                    getSelection: quill.getSelection,
                    getContents: quill.getContents
                });
        }
    }, [quill]);

    const monitoringTextChange = React.useCallback((active: boolean) => {
        if (quill) {
            if (active) quill.on('text-change', quillOnChangeHandle);
            else quill.off('text-change', quillOnChangeHandle);
        }
    }, [quill]);

    useEffect(() => {
        if (quill) {
            var qtr = new QuillTranslateToolbar(id, mprcTheme);
            qtr.translate();

            if (autoFocus) quill.focus();
            if (disabled) quill.disable();
            if (props.initialValue) quill.clipboard.dangerouslyPasteHTML(props.initialValue);
            monitoringTextChange(true);
        }
        return () => {
            monitoringTextChange(false);
        };
    }, [quill]);

    useEffect(() => {
        if (quill) {
            if (disabled) quill.disable();
            else quill.enable();
        }
    }, [disabled]);

    useEffect(() => {
        if (error) document.querySelector(".quill-container-editor")?.classList.add('ql-error');
        else document.querySelector(".quill-container-editor")?.classList.remove('ql-error');
    }, [error]);

    useEffect(() => {
        if (quill && quill.root.innerHTML !== props.initialValue) {
            monitoringTextChange(false);
            quill.root.innerHTML = "";
            quill.clipboard.dangerouslyPasteHTML(props.initialValue ?? "");
            monitoringTextChange(true);
        }
    }, [props.initialValue]);

    return (
        <>
            <div id={id} ref={quillRef} />
            {error && helperText && <FormHelperText error>{helperText}</FormHelperText>}
        </>
    );
};
